import { useEffect, useState } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useQuery, useMutation } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import ShareAccessOptions from '../../components/common/ShareAccessOptions';
import Pagination from '../../components/common/Pagination';
import { SHARED_LINKS_QUERY, PROJECTS_QUERY } from '../../graphql/queries';
import { CREATE_SHARED_LINK_MUTATION, REVOKE_SHARED_LINK_MUTATION } from '../../graphql/mutations';
import { useAuth } from '../../contexts/AuthContext';
import { DEFAULT_SHARE_OPTIONS, shareOptionsInput } from '../../utils/shareLink';
import styles from './SharingCenter.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

const permissionConfig: Record<string, { label: string; color: string; icon: IconName }> = {
  view: { label: 'View Only', color: 'badge-blue', icon: 'visibility' },
  download: { label: 'Download', color: 'badge-green', icon: 'download' },
  comment: { label: 'Comment', color: 'badge-orange', icon: 'comment' },
};

const SHARE_PASSWORD_MIN = 4;
const PAGE_SIZE = 20;

const statusConfig: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'badge-green' },
  expired: { label: 'Expired', color: 'text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded-full' },
  revoked: { label: 'Revoked', color: 'text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full' },
};

const SharingCenter = () => {
  const { user } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = usePageParam();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');
  const [createError, setCreateError] = useState('');
  const [actionError, setActionError] = useState('');
  const [createForm, setCreateForm] = useState({
    projectId: '',
    permission: 'view',
    expiresAt: '',
    maxViews: '',
    recipientEmail: '',
    note: '',
    password: '',
  });
  const [shareOptions, setShareOptions] = useState(DEFAULT_SHARE_OPTIONS);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== search) { setSearch(next); setPage(1); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, loading, error, refetch } = useQuery(SHARED_LINKS_QUERY, {
    variables: { page, limit: PAGE_SIZE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
  });

  const { data: projectsData } = useQuery(PROJECTS_QUERY, { variables: { limit: 100 } });

  const [createLink, { loading: creating }] = useMutation(CREATE_SHARED_LINK_MUTATION, {
    onCompleted: () => {
      setShowCreateModal(false);
      setCreateForm({ projectId: '', permission: 'view', expiresAt: '', maxViews: '', recipientEmail: '', note: '', password: '' });
      setShareOptions(DEFAULT_SHARE_OPTIONS);
      if (page === 1) refetch();
      else setPage(1);
    },
    onError: (e) => setCreateError(e.message),
  });

  const [revokeLink] = useMutation(REVOKE_SHARED_LINK_MUTATION, {
    onCompleted: () => refetch(),
    onError: (e) => setActionError(e.message),
  });

  const openCreateModal = () => {
    setCreateError('');
    setShowCreateModal(true);
  };

  const passwordTooShort = createForm.password.length > 0 && createForm.password.length < SHARE_PASSWORD_MIN;

  const links = data?.sharedLinks?.links || [];
  const total: number = data?.sharedLinks?.total ?? links.length;
  // Over every link the user made, whatever the page or search.
  const stats: { active: number; expired: number; revoked: number; views: number } =
    data?.sharedLinks?.stats ?? { active: 0, expired: 0, revoked: 0, views: 0 };
  const projects = projectsData?.projects?.projects || [];

  const handleCreate = () => {
    if (!createForm.projectId) return;
    setCreateError('');
    createLink({
      variables: {
        input: {
          projectId: createForm.projectId,
          permission: createForm.permission,
          expiresAt: createForm.expiresAt ? new Date(createForm.expiresAt).toISOString() : undefined,
          maxViews: createForm.maxViews ? parseInt(createForm.maxViews) : undefined,
          recipientEmail: createForm.recipientEmail || undefined,
          note: createForm.note || undefined,
          password: createForm.password || undefined,
          ...shareOptionsInput(shareOptions),
        },
      },
    });
  };

  const copyToClipboard = async (token: string) => {
    const url = `${window.location.origin}/share/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 2000);
    } catch {
      setActionError(`Could not copy the link. Copy it by hand: ${url}`);
    }
  };


  return (
    <DashboardLayout onSearch={setSearchInput} searchPlaceholder="Search shared links...">
      <div className="space-y-6">
        <div className={`flex justify-between items-end ${styles.pageHeader}`}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Sharing Center</h2>
            <p className="text-on-surface-variant mt-1">Manage secure access and expiration for your geospatial assets.</p>
          </div>
          <button
            onClick={openCreateModal}
            className="btn-primary px-5 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container"
          >
            <Icon name="add_link" className="text-sm" />
            Create Share Link
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {([
            { label: 'Active Links', value: stats.active, icon: 'link', color: 'text-primary bg-primary/10' },
            { label: 'Total Views', value: stats.views, icon: 'visibility', color: 'text-green-600 bg-green-50' },
            { label: 'Total Links', value: stats.active + stats.expired + stats.revoked, icon: 'share', color: 'text-secondary bg-secondary-container/30' },
            { label: 'Revoked', value: stats.revoked, icon: 'link_off', color: 'text-error bg-error-container' },
          ] as const).map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl p-5">
              <div className={`p-2 ${stat.color} rounded-lg w-fit mb-3`}>
                <Icon name={stat.icon} />
              </div>
              <p className="text-xs text-on-surface-variant uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-bold mt-1">{stat.value}</p>
            </div>
          ))}
        </div>

        {actionError && (
          <div className={styles.errorBanner} role="alert">
            <span>{actionError}</span>
            <button onClick={() => setActionError('')} className={styles.errorDismiss} title="Dismiss">
              <Icon name="close" className="text-base" />
            </button>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading links...
          </div>
        ) : error && !data ? (
          <div className="bg-white rounded-xl p-16 text-center" role="alert">
            <Icon name="error" className="text-5xl text-error mb-4 block" />
            <p className="text-on-surface-variant">Could not load your shared links: {error.message}</p>
            <button onClick={() => refetch()} className="mt-4 text-primary hover:underline text-sm font-medium">
              Try again
            </button>
          </div>
        ) : links.length === 0 ? (
          <div className="bg-white rounded-xl p-16 text-center">
            <Icon name="link_off" className="text-5xl text-outline mb-4 block" />
            <p className="text-on-surface-variant">
              {search
                ? 'No shared links match your search.'
                : 'No shared links yet. Create one to share your files securely.'}
            </p>
          </div>
        ) : (
          <div className={`bg-white rounded-xl ${styles.tableWrap}`}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-surface-container-low">
                  <th className="table-header">Project</th>
                  <th className="table-header">Link Token</th>
                  <th className="table-header">Views</th>
                  <th className="table-header">Expires</th>
                  <th className="table-header">Status</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10">
                {links.map((link: { id: string; token: string; project: { name: string }; permission: string; viewCount: number; maxViews?: number; expiresAt?: string; status: string; recipientEmail?: string; note?: string; passwordProtected?: boolean }) => (
                  <tr key={link.id} className="table-row">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-on-surface">{link.project.name}</p>
                      {link.recipientEmail && (
                        <p className="text-xs text-on-surface-variant">{link.recipientEmail}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className={styles.tokenField}>
                        {link.passwordProtected && (
                          <Icon name="lock" className="text-sm text-on-surface-variant flex-shrink-0" title="Password protected" />
                        )}
                        <span className="text-xs font-mono text-on-surface-variant truncate max-w-[120px]">{link.token.slice(0, 12)}...</span>
                        <button
                          onClick={() => copyToClipboard(link.token)}
                          className="text-primary hover:text-primary-container transition-colors ml-auto flex-shrink-0"
                          title="Copy link"
                        >
                          <Icon name={copiedToken === link.token ? 'check' : 'content_copy'} className="text-sm" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {link.viewCount}{link.maxViews ? `/${link.maxViews}` : ''}
                    </td>
                    <td className="px-6 py-4 text-xs text-on-surface-variant">
                      {link.expiresAt ? new Date(link.expiresAt).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={statusConfig[link.status]?.color}>{statusConfig[link.status]?.label}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => copyToClipboard(link.token)}
                          className="p-2 hover:bg-primary/10 rounded text-primary transition-colors"
                          title="Copy link"
                        >
                          <Icon name="content_copy" className="text-lg" />
                        </button>
                        {link.status === 'active' && (
                          <button
                            onClick={() => revokeLink({ variables: { id: link.id } })}
                            className="p-2 hover:bg-error/10 rounded text-error transition-colors"
                            title="Revoke link"
                          >
                            <Icon name="link_off" className="text-lg" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && (
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="links" variant="card" />
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Share Link"
        footer={
          <>
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !createForm.projectId || passwordTooShort}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Link'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="share-project" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Project *</label>
            <select id="share-project" className="input-field" value={createForm.projectId} onChange={(e) => setCreateForm((f) => ({ ...f, projectId: e.target.value }))}>
              <option value="">— Select project —</option>
              {projects.map((p: { id: string; name: string }) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="share-expires" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Expires At</label>
              <input id="share-expires" className="input-field" type="datetime-local" value={createForm.expiresAt} onChange={(e) => setCreateForm((f) => ({ ...f, expiresAt: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="share-max-views" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Max Views</label>
              <input id="share-max-views" className="input-field" type="number" placeholder="Unlimited" min="1" value={createForm.maxViews} onChange={(e) => setCreateForm((f) => ({ ...f, maxViews: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="share-recipient" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Recipient Email</label>
            <input id="share-recipient" className="input-field" type="email" placeholder="optional@email.com" value={createForm.recipientEmail} onChange={(e) => setCreateForm((f) => ({ ...f, recipientEmail: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="share-note" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Note</label>
            <textarea id="share-note" className="input-field resize-none" rows={2} placeholder="Optional note about this link..." value={createForm.note} onChange={(e) => setCreateForm((f) => ({ ...f, note: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="share-password" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Password</label>
            <input id="share-password" className="input-field" type="text" autoComplete="off" spellCheck={false} maxLength={64} placeholder="Optional — leave empty for no password" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} />
            <p className={`text-xs ${passwordTooShort ? 'text-error' : 'text-on-surface-variant'}`}>
              {passwordTooShort
                ? `Use at least ${SHARE_PASSWORD_MIN} characters`
                : 'Recipients must enter it to open the link. It cannot be viewed later.'}
            </p>
          </div>
          <ShareAccessOptions value={shareOptions} onChange={setShareOptions} />
          {createError && <p className="text-sm text-error">{createError}</p>}
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default SharingCenter;
